import AddLocationAlt from "@mui/icons-material/AddLocationAlt";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Fab,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GoogleMap, InfoWindow, LoadScriptNext } from "@react-google-maps/api";
import { Circle, Marker } from "../theme/maps";
import React, { useEffect, useMemo, useRef, useState } from "react";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { useDispatch } from "react-redux";
import { addMarkerCount, deleteMarkerCount } from "../redux/features/coverageSlice";
import { getMyLocation, milesToKm } from "../utils";
import { buildCoveragePayload } from "./buildPayloadCoverage";

const DEFAULT_CENTER = { lat: 39.748116, lng: -104.770598 };
const DEFAULT_RADIUS_METERS = 16093.4; // 10 miles
const MIN_MILES = 1;
const MAX_MILES = 10;

const metersToMiles = (m) => m / 1609.34;
const milesToMeters = (mi) => mi * 1609.34;
const newId = (() => {
  let c = 0;
  return () => `${Date.now().toString(36)}_${(c++).toString(36)}`;
})();

const COLORS = {
  coverage: { stroke: "#FF3B30", fill: "#FF3B30" },
  ignore: { stroke: "#6A5ACD", fill: "#6A5ACD" },
  disabled: { stroke: "var(--fx-surface-textsecondary, #64748B)", fill: "var(--fx-surface-textsecondary, #64748B)" },
};

const pinIcon = (color, holeColor) => ({
  url: `data:image/svg+xml;charset=UTF-8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24'><path fill='${color}' d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/><circle cx='12' cy='9.1' r='2.7' fill='${holeColor}' /></svg>`,
});

const PINS = {
  coverage: pinIcon("rgba(234,67,53,1)", "rgba(180,20,18,1)"),
  ignore: pinIcon("rgba(106,90,205,1)", "rgba(70,60,160,1)"),
  disabled: pinIcon("gray", "#545454"),
};

const Mode = {
  IDLE: "idle",
  PLACE_COVERAGE: "placeCoverage",
  PLACE_IGNORE: "placeIgnore",
  RESIZE_COVERAGE: "resizeCoverage",
  RESIZE_IGNORE: "resizeIgnore",
};

const fmtMi = (val) => `${Math.round(val)} mi`;

// ------------------------------ Componente ------------------------------
export const MapComponent = ({
  updateEntityState,
  entityState,
  isSignupProcess = false,
  setChanges = () => { },
  refresh = () => { },
  refreshCb = () => { },
  setData = () => { },
  setIgnoreData = () => { },
  setLoading = () => { },
  markersLimit = undefined,
  ignoreLimit = undefined,
  isInformative = false,
  zoom = 7,
  streetViewControl = false,
  mapContainerStyle = undefined,
  updated = () => { },
  changes,
  handleUpdateMyAreas = () => { },
  handleCbActionFactory = () => { },
  setCancel = () => { },
  fullWidth = true,
}) => {
  const [markers, setMarkers] = useState([]);
  const [ignoreAreas, setIgnoreAreas] = useState([]);
  const [markersBackup, setMarkersBackup] = useState([]);
  const [ignoreBackup, setIgnoreBackup] = useState([]);

  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [address, setAddress] = useState("");

  const [activeCoverageId, setActiveCoverageId] = useState(null);
  const [activeIgnoreId, setActiveIgnoreId] = useState(null);
  const [mode, setMode] = useState(Mode.IDLE);

  const [isCoverageInfoOpen, setCoverageInfoOpen] = useState(false);
  const [isIgnoreInfoOpen, setIgnoreInfoOpen] = useState(false);

  const [showSidebar, setShowSidebar] = useState(true);
  const [expandedCoverage, setExpandedCoverage] = useState({});

  const [popupLocked, setPopupLocked] = useState(false);

  const [editingCoverageId, setEditingCoverageId] = useState(null);
  const [draftCoverageName, setDraftCoverageName] = useState("");

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const dispatch = useDispatch();

  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const lastGeocodeReq = useRef(0);

  const containerStyle = useMemo(
    () => ({
      width: showSidebar ? "800px" : "1200px",
      height: isMd ? "630px" : "900px",
      borderRadius: 12,
    }),
    [isMd, showSidebar],
  );

  const reindex = (arr) => arr.map((m, i) => ({ ...m, index: i }));

  const setupGeocoder = () => {
    if (!geocoderRef.current && window.google?.maps) geocoderRef.current = new window.google.maps.Geocoder();
  };

  const geocodeLatLng = async (lat, lng) => {
    if (!window.google?.maps) return null;
    setupGeocoder();
    const myReq = ++lastGeocodeReq.current;
    try {
      const { results } = await geocoderRef.current.geocode({ location: { lat, lng } });
      if (myReq !== lastGeocodeReq.current) return null;
      const top = results?.[0];
      const comps = top?.address_components || [];
      const find = (t) => comps.find((c) => c.types.includes(t))?.long_name || "";
      return {
        address: top?.formatted_address || "",
        city: find("locality") || find("sublocality") || find("postal_town"),
        state: find("administrative_area_level_1"),
        country: find("country"),
      };
    } catch {
      return null;
    }
  };

  // --- Geometry helpers ---
  const spherical = () => window.google?.maps?.geometry?.spherical;
  const toLL = (p) => new window.google.maps.LatLng(p.lat, p.lng);

  const clampIgnoreToCoverage = (coverage, ignore) => {
    if (!coverage) return ignore;
    const sph = spherical();
    let radius = Math.min(ignore.radius ?? DEFAULT_RADIUS_METERS, Math.max(coverage.radius - 1, 10));
    if (!sph) return { ...ignore, radius };
    const center = toLL(coverage);
    const point = toLL(ignore);
    const d = sph.computeDistanceBetween(center, point);
    const maxCenterDist = Math.max(0, coverage.radius - radius);
    if (d <= maxCenterDist) return { ...ignore, radius };
    const heading = sph.computeHeading(center, point);
    const clamped = sph.computeOffset(center, maxCenterDist, heading);
    return { ...ignore, lat: clamped.lat(), lng: clamped.lng(), radius };
  };

  const maxIgnoreRadiusMeters = (coverage, ignoreCenter) => {
    const sph = spherical();
    if (!coverage || !sph) return DEFAULT_RADIUS_METERS;
    const d = sph.computeDistanceBetween(toLL(coverage), toLL(ignoreCenter));
    return Math.max(coverage.radius - d - 1, 10);
  };

  const buildArea = async ({ lat, lng, base, presetName, parentId = null }) => {
    setLoading(true);
    try {
      const details = await geocodeLatLng(lat, lng);
      return {
        id: base?.id || newId(),
        lat,
        lng,
        radius: base?.radius ?? DEFAULT_RADIUS_METERS,
        isAvailable: true,
        name: presetName || base?.name || "Location",
        selected: false,
        isDefault: !!base?.isDefault,
        address: details?.address || base?.address || "",
        city: details?.city || base?.city || "",
        state: details?.state || base?.state || "",
        country: details?.country || base?.country || "",
        parentId: base?.parentId ?? parentId ?? null,
      };
    } finally {
      setLoading(false);
    }
  };

  const syncAndFlag = (nextMarkers, nextIgnores) => {
    const cov = reindex(nextMarkers);
    const ign = reindex(nextIgnores);
    setMarkers(cov);
    setIgnoreAreas(ign);
    setData(cov);
    setIgnoreData(ign);
    if (typeof updateEntityState === "function") {
      dispatch(updateEntityState({ coverageAreas: cov, ignoreAreas: ign }));
    }
    const changed = JSON.stringify(cov) !== JSON.stringify(markersBackup) || JSON.stringify(ign) !== JSON.stringify(ignoreBackup);
    setChanges(changed);
  };

  const toNumber = (v) => (v == null ? undefined : Number(v));

  const updateFromLocations = (locations = [], ignores = []) => {
    locations.forEach(() => dispatch(addMarkerCount()));

    const cov = locations.map((loc, idx) => {
      const lat = toNumber(loc.lat ?? loc.latitude);
      const lng = toNumber(loc.lng ?? loc.longitude);
      const miles = toNumber(loc.radius ?? loc.milesOfCoverageArea);

      return {
        lat,
        lng,
        address: loc.address,
        city: loc.city,
        state: loc.state,
        country: loc.country,
        radius: miles,
        id: loc?.id || newId(),
        isAvailable: loc.isAvailable ?? true,
        name: loc.name ? loc.name : `Location: ${idx + 1}`,
        index: idx,
        selected: false,
        isDefault: !!loc?.isDefault,
      };
    });

    const defaultParent = cov[0]?.id ?? null;

    const ign = ignores.map((loc, idx) => {
      const lat = toNumber(loc.lat ?? loc.latitude);
      const lng = toNumber(loc.lng ?? loc.longitude);
      const miles = toNumber(loc.radius ?? loc.milesOfCoverageArea);
      const radius = miles != null ? miles : DEFAULT_RADIUS_METERS;
      const parentId = loc.parentId ?? loc.parentCoverageAreaId ?? defaultParent;

      return {
        lat,
        lng,
        address: loc.address,
        city: loc.city,
        state: loc.state,
        country: loc.country,
        radius,
        id: loc?.id || newId(),
        isAvailable: loc.isAvailable ?? true,
        name: loc.name ? loc.name : `Ignore: ${idx + 1}`,
        index: idx,
        selected: false,
        isDefault: false,
        parentId,
      };
    });

    const clamped = ign.map((ig) =>
      clampIgnoreToCoverage(
        cov.find((c) => c.id === ig.parentId),
        ig,
      ),
    );
    syncAndFlag(cov, clamped);

    setMarkersBackup(cov);
    setIgnoreBackup(clamped);

    if (cov.length) {
      setMapCenter({ lat: cov[0].lat, lng: cov[0].lng });
      setActiveCoverageId(cov[0].id);
      setExpandedCoverage({ [cov[0].id]: true });
    }
  };

  const retrieve = () => {
    setLoading(true);
    if (!isSignupProcess) {
      const token = sessionStorage.getItem("token");
      getMyLocation(token)
        .then((res = []) => {
          // res es tu array de coverage areas (cada uno con .ignoreAreas)
          const locations = res;
          const ignores = res.flatMap((cov) =>
            (cov.ignoreAreas || []).map((ig) => ({
              ...ig,
              // Asegura relación padre-hijo
              parentId: ig.parentId ?? ig.parentCoverageAreaId ?? cov.id,
            })),
          );
          updateFromLocations(locations, ignores);
        })
        .finally(() => {
          setLoading(false);
          refreshCb?.();
        });
    } else {
      const { coverageAreas = [], ignoreAreas: ign = [] } = entityState || {};
      updateFromLocations(coverageAreas, ign);
      setLoading(false);
    }
  };

  useEffect(() => {
    retrieve(); /* eslint-disable-next-line */
  }, [updated]);
  useEffect(() => {
    retrieve(); /* eslint-disable-next-line */
  }, [refresh]);

  const activeCoverage = useMemo(() => markers.find((m) => m.id === activeCoverageId) || null, [markers, activeCoverageId]);
  const activeIgnore = useMemo(() => ignoreAreas.find((i) => i.id === activeIgnoreId) || null, [ignoreAreas, activeIgnoreId]);
  const visibleIgnores = useMemo(
    () => (activeCoverageId ? ignoreAreas.filter((i) => i.parentId === activeCoverageId) : []),
    [ignoreAreas, activeCoverageId],
  );

  // --------------------------- Acciones ---------------------------
  const startPlaceCoverage = () => {
    setMode(Mode.PLACE_COVERAGE);
    setActiveIgnoreId(null);
    setPopupLocked(false);
    setCoverageInfoOpen(false);
  };

  const startPlaceIgnore = () => {
    if (activeCoverage) {
      setMode(Mode.PLACE_IGNORE);
      setActiveIgnoreId(null);
      setPopupLocked(true);
      setCoverageInfoOpen(true); // abre para mostrar la alerta
    }
  };

  const startResizeCoverage = () => {
    if (activeCoverage) {
      setMode(Mode.RESIZE_COVERAGE);
      setPopupLocked(false);
      setCoverageInfoOpen(true);
    }
  };

  const startResizeIgnore = (id) => {
    setActiveIgnoreId(id);
    setMode(Mode.RESIZE_IGNORE);
    setPopupLocked(true);
    setIgnoreInfoOpen(true);
  };

  const clearFocus = () => {
    setMode(Mode.IDLE);
    setActiveIgnoreId(null);
    setPopupLocked(false);
    setCoverageInfoOpen(false);
    setIgnoreInfoOpen(false);
  };

  const selectCoverage = (id) => {
    setActiveCoverageId(id);
    setActiveIgnoreId(null);
    setMode(Mode.IDLE);
    setPopupLocked(false);
    const coverage = markers.find((m) => m.id === id);
    if (coverage) setMapCenter({ lat: coverage.lat, lng: coverage.lng });
    setCoverageInfoOpen(true);
    // cerrar edición si está activa
    setEditingCoverageId(null);
    setDraftCoverageName("");
  };

  const toggleCoverageExpansion = (id) => {
    setExpandedCoverage((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const deleteCoverage = (id) => {
    const nextMarkers = markers.filter((m) => m.id !== id);
    const nextIgnores = ignoreAreas.filter((ig) => ig.parentId !== id);
    syncAndFlag(nextMarkers, nextIgnores);
    if (activeCoverageId === id) {
      const newActive = nextMarkers[0]?.id || null;
      setActiveCoverageId(newActive);
      if (newActive) {
        setExpandedCoverage((prev) => ({ ...prev, [newActive]: true }));
      }
    }
    // reset edición si aplica
    if (editingCoverageId === id) {
      setEditingCoverageId(null);
      setDraftCoverageName("");
    }
    dispatch(deleteMarkerCount());
    setPopupLocked(false);
  };

  const deleteIgnore = (id) => {
    syncAndFlag(
      markers,
      ignoreAreas.filter((i) => i.id !== id),
    );
    if (activeIgnoreId === id) {
      setActiveIgnoreId(null);
      setMode(Mode.IDLE);
      setPopupLocked(false);
    }
  };

  const changeCoverageRadiusMiles = (miles) => {
    if (!activeCoverage) return;
    const meters = milesToMeters(Number(miles));
    const nextMarkers = markers.map((m) => (m.id === activeCoverage.id ? { ...m, radius: meters } : m));
    const fixedIg = ignoreAreas.map((ig) =>
      ig.parentId === activeCoverage.id ? clampIgnoreToCoverage({ ...activeCoverage, radius: meters }, ig) : ig,
    );
    syncAndFlag(nextMarkers, fixedIg);
  };

  const changeIgnoreRadiusMiles = (miles) => {
    const ig = activeIgnore;
    const cov = activeCoverage;
    if (!ig || !cov) return;
    const metersCandidate = milesToMeters(Number(miles));
    const maxMeters = maxIgnoreRadiusMeters(cov, ig);
    const meters = Math.min(metersCandidate, maxMeters);
    const clamped = clampIgnoreToCoverage(cov, { ...ig, radius: meters });
    const next = ignoreAreas.map((x) => (x.id === ig.id ? clamped : x));
    syncAndFlag(markers, next);
  };

  // Colocar ignore en un punto específico
  const placeIgnoreAt = async (lat, lng, parent) => {
    if (ignoreLimit !== undefined && visibleIgnores.length >= ignoreLimit) return;
    let built = await buildArea({ lat, lng, presetName: `Ignore: ${visibleIgnores.length + 1}`, parentId: parent.id });
    built = clampIgnoreToCoverage(parent, built);
    const nextIg = [...ignoreAreas, built];
    syncAndFlag(markers, nextIg);
    setActiveIgnoreId(built.id);
    setMode(Mode.RESIZE_IGNORE);
    setPopupLocked(true);
    setIgnoreInfoOpen(true);
  };

  // Renombrado coverage
  const startEditCoverageName = (id, currentName) => {
    setEditingCoverageId(id);
    setDraftCoverageName(currentName || "");
  };

  const commitEditCoverageName = () => {
    if (!editingCoverageId) return;
    const name = draftCoverageName.trim();
    if (!name) {
      setEditingCoverageId(null);
      setDraftCoverageName("");
      return;
    }
    const nextMarkers = markers.map((m) => (m.id === editingCoverageId ? { ...m, name } : m));
    syncAndFlag(nextMarkers, ignoreAreas);
    setEditingCoverageId(null);
    setDraftCoverageName("");
  };

  const cancelEditCoverageName = () => {
    setEditingCoverageId(null);
    setDraftCoverageName("");
  };

  // --------------------------- Interacciones del mapa ---------------------------
  const onMapClick = async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    if (mode === Mode.PLACE_COVERAGE) {
      if (markersLimit !== undefined && markers.length >= markersLimit) return;
      const built = await buildArea({ lat, lng, presetName: `Location: ${markers.length + 1}` });
      const next = [...markers, built];
      syncAndFlag(next, ignoreAreas);
      setActiveCoverageId(built.id);
      setMode(Mode.RESIZE_COVERAGE);
      setExpandedCoverage((prev) => ({ ...prev, [built.id]: true }));
      setCoverageInfoOpen(true);
    } else if (mode === Mode.PLACE_IGNORE && activeCoverage) {
      await placeIgnoreAt(lat, lng, activeCoverage);
    }
  };

  const onCircleClick = async (coverage, e) => {
    if (mode === Mode.PLACE_IGNORE && activeCoverage && activeCoverage.id === coverage.id && e?.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      await placeIgnoreAt(lat, lng, coverage);
    } else if (!popupLocked) {
      setActiveCoverageId(coverage.id);
      setActiveIgnoreId(null);
      setMode(Mode.IDLE);
      setExpandedCoverage((prev) => ({ ...prev, [coverage.id]: true }));
      setCoverageInfoOpen(true);
    }
  };

  const onIgnoreClick = (ignore, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    setActiveIgnoreId(ignore.id);
    setMode(Mode.RESIZE_IGNORE);
    setPopupLocked(true);
    setIgnoreInfoOpen(true);
  };

  const onDragEnd = async (type, idx, e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    if (type === "coverage") {
      const base = markers[idx];
      const updated = await buildArea({ lat, lng, base });
      const next = markers.map((m, i) => (i === idx ? { ...m, ...updated } : m));
      const fixedIg = ignoreAreas.map((ig) => (ig.parentId === base.id ? clampIgnoreToCoverage(updated, ig) : ig));
      syncAndFlag(next, fixedIg);
      setActiveCoverageId(base.id);
    } else {
      const base = ignoreAreas[idx];
      const parent = markers.find((c) => c.id === base.parentId) || activeCoverage;
      let updated = await buildArea({ lat, lng, base });
      updated = clampIgnoreToCoverage(parent, updated);
      const next = ignoreAreas.map((m, i) => (i === idx ? { ...m, ...updated } : m));
      syncAndFlag(markers, next);
      setActiveIgnoreId(base.id);
      setPopupLocked(true);
    }
  };

  // ------------------------------ Render ------------------------------
  return (
    <LoadScriptNext googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={["places", "geometry"]} onLoad={setupGeocoder}>
      <Box sx={{ display: "flex", position: "relative" }}>
        {/* Contenido principal del mapa */}
        <Box sx={{ flex: 1 }}>
          {/* Barra superior de búsqueda y acciones */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5 }} alignItems={{ xs: "stretch", md: "center" }}>
            <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: "1px solid", borderColor: "divider", flex: 1 }}>
              <PlacesAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={async (value) => {
                  setAddress(value);
                  try {
                    const results = await geocodeByAddress(value);
                    const latLng = await getLatLng(results[0]);
                    setMapCenter(latLng);
                  } catch { }
                }}
              >
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      fullWidth
                      {...getInputProps({ label: "Search a place", placeholder: "Enter an address" })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                    />
                    {(loading || suggestions.length > 0) && (
                      <Paper
                        elevation={3}
                        style={{
                          position: "absolute",
                          zIndex: 2147483647,
                          width: "100%",
                          marginTop: 8,
                          maxHeight: 280,
                          overflowY: "auto",
                          borderRadius: 8,
                        }}
                      >
                        {loading && (
                          <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Loading…
                            </Typography>
                          </Box>
                        )}
                        {suggestions.map((s) => {
                          const style = { backgroundColor: s.active ? "var(--fx-surface-sunken, #F8FAFC)" : "var(--fx-surface-base, #FFFFFF)", cursor: "pointer", padding: "10px 16px" };
                          return (
                            <div key={s.placeId || s.description} {...getSuggestionItemProps(s, { style })}>
                              {s.description}
                            </div>
                          );
                        })}
                      </Paper>
                    )}
                  </Box>
                )}
              </PlacesAutocomplete>
            </Paper>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Fab
                size="small"
                color={mode === Mode.PLACE_COVERAGE ? "primary" : "default"}
                onClick={startPlaceCoverage}
                title="Add coverage"
                disabled={isInformative}
              >
                <AddLocationAlt fontSize="small" />
              </Fab>
              <Tooltip title="Center on active coverage">
                <span>
                  <Fab
                    size="small"
                    color="default"
                    disabled={!activeCoverage}
                    onClick={() => activeCoverage && setMapCenter({ lat: activeCoverage.lat, lng: activeCoverage.lng })}
                  >
                    <MyLocationIcon fontSize="small" />
                  </Fab>
                </span>
              </Tooltip>
              <Tooltip title="Toggle sidebar">
                <Fab size="small" color="default" onClick={() => setShowSidebar(!showSidebar)}>
                  {showSidebar ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </Fab>
              </Tooltip>
              <Chip label={`Coverages: ${markers.length}`} size="small" />
              {activeCoverage && <Chip label={`Ignores: ${visibleIgnores.length}`} size="small" color="secondary" />}
            </Stack>
          </Stack>

          {/* Mapa */}
          <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <GoogleMap
              onLoad={(m) => (mapRef.current = m)}
              mapContainerStyle={mapContainerStyle || containerStyle}
              center={mapCenter}
              options={{ fullscreenControl: false, streetViewControl }}
              zoom={zoom}
              onClick={onMapClick}
            >
              {/* Coverage layers */}
              {markers.map((m, idx) => (
                <React.Fragment key={m.id}>
                  <Marker
                    position={{ lat: m.lat, lng: m.lng }}
                    draggable={!isInformative}
                    onDragEnd={(e) => onDragEnd("coverage", idx, e)}
                    onClick={() => !popupLocked && selectCoverage(m.id)}
                    icon={PINS.coverage}
                    zIndex={2}
                  />
                  <Circle
                    center={{ lat: m.lat, lng: m.lng }}
                    radius={m.radius}
                    onClick={(e) => onCircleClick(m, e)}
                    options={{
                      strokeColor: COLORS.coverage.stroke,
                      strokeOpacity: m.id === activeCoverageId ? 0.95 : 0.6,
                      strokeWeight: m.id === activeCoverageId ? 3 : 2,
                      fillColor: COLORS.coverage.fill,
                      fillOpacity: m.id === activeCoverageId ? 0.3 : 0.16,
                      zIndex: 1,
                    }}
                  />
                </React.Fragment>
              ))}

              {/* Ignore solo del coverage activo */}
              {visibleIgnores.map((m) => {
                const idx = ignoreAreas.findIndex((x) => x.id === m.id);
                return (
                  <React.Fragment key={m.id}>
                    <Marker
                      position={{ lat: m.lat, lng: m.lng }}
                      draggable={!isInformative}
                      onDragEnd={(e) => onDragEnd("ignore", idx, e)}
                      onClick={(e) => onIgnoreClick(m, e)}
                      icon={PINS.ignore}
                      zIndex={4}
                    />
                    <Circle
                      center={{ lat: m.lat, lng: m.lng }}
                      radius={m.radius}
                      onClick={(e) => {
                        onIgnoreClick(m, e);
                      }}
                      options={{
                        strokeColor: COLORS.ignore.stroke,
                        strokeOpacity: m.id === activeIgnoreId ? 0.9 : 0.7,
                        strokeWeight: m.id === activeIgnoreId ? 3 : 2,
                        fillColor: COLORS.ignore.fill,
                        fillOpacity: m.id === activeIgnoreId ? 0.35 : 0.22,
                        zIndex: 3,
                      }}
                    />
                  </React.Fragment>
                );
              })}

              {/* Coverage InfoWindow (reabrible) */}
              {activeCoverage && !activeIgnore && isCoverageInfoOpen && (
                <InfoWindow
                  position={{ lat: activeCoverage.lat, lng: activeCoverage.lng }}
                  onCloseClick={() => setCoverageInfoOpen(false)}
                  options={{
                    disableAutoPan: true,
                    zIndex: 3000,
                    pixelOffset: window.google?.maps ? new window.google.maps.Size(0, -40) : undefined,
                  }}
                >
                  <Paper elevation={8} sx={{ p: 2, minWidth: 280, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
                      {activeCoverage.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Coverage Area
                    </Typography>
                    <Box sx={{ my: 1.5 }} />

                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant={mode === Mode.PLACE_IGNORE ? "contained" : "outlined"}
                          startIcon={<BlockIcon />}
                          onClick={startPlaceIgnore}
                          color="secondary"
                          disabled={isInformative || (ignoreLimit !== undefined && visibleIgnores.length >= ignoreLimit)}
                        >
                          Add ignore area
                        </Button>
                        <Button
                          size="small"
                          variant={mode === Mode.RESIZE_COVERAGE ? "contained" : "outlined"}
                          onClick={startResizeCoverage}
                          disabled={isInformative}
                        >
                          Resize
                        </Button>
                      </Stack>

                      {mode === Mode.PLACE_IGNORE && <Alert severity="info">Click inside the red circle to place an ignore area.</Alert>}

                      {mode === Mode.RESIZE_COVERAGE && (
                        <Box sx={{ px: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Radius: {Math.round(metersToMiles(activeCoverage.radius))} miles
                          </Typography>
                          <Slider
                            size="small"
                            min={MIN_MILES}
                            max={MAX_MILES}
                            value={Math.round(metersToMiles(activeCoverage.radius))}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${v} mi`}
                            onChange={(_, v) => changeCoverageRadiusMiles(v)}
                          />
                        </Box>
                      )}

                      <Stack direction="row" justifyContent="space-between">
                        <Button size="small" onClick={clearFocus}>
                          Close
                        </Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteCoverage(activeCoverage.id)}>
                          Delete
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </InfoWindow>
              )}

              {/* Ignore InfoWindow (reabrible) */}
              {activeIgnore && isIgnoreInfoOpen && (
                <InfoWindow
                  position={{ lat: activeIgnore.lat, lng: activeIgnore.lng }}
                  onCloseClick={() => setIgnoreInfoOpen(false)}
                  options={{
                    disableAutoPan: true,
                    zIndex: 3000,
                    pixelOffset: window.google?.maps ? new window.google.maps.Size(0, -40) : undefined,
                  }}
                >
                  <Paper elevation={8} sx={{ p: 2, minWidth: 260, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "secondary.main" }}>
                      {activeIgnore.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ignore Area
                    </Typography>
                    <Box sx={{ my: 1.5 }} />
                    <Box sx={{ px: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Radius: {Math.round(metersToMiles(activeIgnore.radius))} miles
                      </Typography>
                      <Slider
                        size="small"
                        min={MIN_MILES}
                        max={Math.max(MIN_MILES, Math.floor(metersToMiles(maxIgnoreRadiusMeters(activeCoverage, activeIgnore))) || MIN_MILES)}
                        value={Math.min(
                          Math.round(metersToMiles(activeIgnore.radius)),
                          Math.max(MIN_MILES, Math.floor(metersToMiles(maxIgnoreRadiusMeters(activeCoverage, activeIgnore))) || MIN_MILES),
                        )}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v} mi`}
                        onChange={(_, v) => changeIgnoreRadiusMiles(v)}
                      />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                      <Button size="small" onClick={clearFocus}>
                        Done
                      </Button>
                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteIgnore(activeIgnore.id)}>
                        Delete
                      </Button>
                    </Stack>
                  </Paper>
                </InfoWindow>
              )}
            </GoogleMap>
          </Paper>
        </Box>

        {/* Sidebar derecho mejorado */}
        {showSidebar && (
          <Box sx={{ width: 360, pl: 1.5 }}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>
                    Coverage areas
                  </Typography>
                  <Chip size="small" label={markers.length} sx={{ fontWeight: 600 }} />
                </Stack>
                <Tooltip title="Add coverage">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={startPlaceCoverage}
                      disabled={isInformative || (markersLimit !== undefined && markers.length >= markersLimit)}
                    >
                      <AddLocationAlt />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {markers.length === 0 && <Alert severity="info">No coverage areas yet. Click the red pin button to add one.</Alert>}

              <List dense sx={{ overflowY: "auto" }}>
                {markers.map((m) => {
                  const isActive = m.id === activeCoverageId;
                  const children = ignoreAreas.filter((i) => i.parentId === m.id);
                  const isEditing = editingCoverageId === m.id;

                  return (
                    <React.Fragment key={m.id}>
                      <ListItem
                        selected={isActive}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          border: "1px solid",
                          borderColor: isActive ? "primary.main" : "divider",
                          bgcolor: isActive ? "action.hover" : "background.paper",
                        }}
                        button
                        onClick={() => selectCoverage(m.id)}
                      >
                        <ListItemIcon>
                          <LocationOnIcon color={isActive ? "primary" : "action"} />
                        </ListItemIcon>

                        {/* Nombre editable */}
                        <ListItemText
                          primary={
                            isEditing ? (
                              <Stack direction="row" spacing={1} alignItems="center" onClick={(e) => e.stopPropagation()}>
                                <TextField
                                  autoFocus
                                  size="small"
                                  value={draftCoverageName}
                                  placeholder="Location name"
                                  onChange={(e) => setDraftCoverageName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.stopPropagation();
                                      commitEditCoverageName();
                                    }
                                    if (e.key === "Escape") {
                                      e.stopPropagation();
                                      cancelEditCoverageName();
                                    }
                                  }}
                                  sx={{ maxWidth: 180 }}
                                />
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    commitEditCoverageName();
                                  }}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditCoverageName();
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography fontWeight={700} noWrap title={m.name}>
                                  {m.name}
                                </Typography>
                                {m.isDefault && <Chip size="small" label="Default" />}
                              </Stack>
                            )
                          }
                          secondary={`${fmtMi(metersToMiles(m.radius))}`}
                        />

                        <Chip size="small" label={`${children.length}`} sx={{ mr: 1 }} />

                        {/* Botón editar nombre */}
                        {!isEditing && (
                          <Tooltip title="Rename">
                            <IconButton
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditCoverageName(m.id, m.name);
                              }}
                              sx={{ mr: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCoverageExpansion(m.id);
                          }}
                        >
                          {expandedCoverage[m.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>

                        <ListItemSecondaryAction>
                          <Tooltip title="Delete">
                            <IconButton
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCoverage(m.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>

                      <Collapse in={!!expandedCoverage[m.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 6, pr: 1, py: 1 }}>
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Button
                              size="small"
                              startIcon={<BlockIcon />}
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                selectCoverage(m.id);
                                startPlaceIgnore();
                              }}
                              disabled={isInformative || (ignoreLimit !== undefined && children.length >= ignoreLimit)}
                            >
                              Add ignore
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                selectCoverage(m.id);
                                startResizeCoverage();
                              }}
                              disabled={isInformative}
                            >
                              Resize
                            </Button>
                          </Stack>

                          {children.length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              No ignores.
                            </Typography>
                          )}

                          <List dense>
                            {children.map((ig) => (
                              <ListItem
                                key={ig.id}
                                sx={{ borderRadius: 1 }}
                                button
                                onClick={() => {
                                  selectCoverage(m.id);
                                  startResizeIgnore(ig.id);
                                }}
                              >
                                <ListItemIcon>
                                  <RadioButtonUncheckedIcon color={ig.id === activeIgnoreId ? "secondary" : "action"} />
                                </ListItemIcon>
                                <ListItemText primary={ig.name} secondary={`${fmtMi(metersToMiles(ig.radius))}`} />
                                <ListItemSecondaryAction>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      edge="end"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteIgnore(ig.id);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Collapse>
                    </React.Fragment>
                  );
                })}
              </List>

              {/* Controles de guardado (opcional, ajusta a tus props reales) */}
              {typeof changes !== "undefined" && (
                <Box sx={{ mt: 1 }}>
                  {changes ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      You have unsaved changes.
                    </Alert>
                  ) : (
                    <Alert severity="success" sx={{ mb: 1 }}>
                      All changes saved.
                    </Alert>
                  )}
                  {typeof setChanges === "function" && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setMarkersBackup(markers);
                          setIgnoreBackup(ignoreAreas);
                          setChanges(false);
                          const payloadMiles = buildCoveragePayload({
                            markers,
                            ignoreAreas,
                            units: "miles",
                          });
                          handleUpdateMyAreas(payloadMiles);
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => {
                          syncAndFlag(markersBackup, ignoreBackup);
                          setChanges(false);
                        }}
                      >
                        Revert
                      </Button>
                    </Stack>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </LoadScriptNext>
  );
};
